/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHAT OUR SCREENING FOUND NEVER REACHES THE CLIENT (v1_967).
 *
 * Walk w7f1c51de (next route, 04/10/2026, at 2e630b6): the drafting pass wrote
 * two client request lines saying "our screening returned a possible match on
 * Tobias Wren Ashcombe that still needs to be resolved". Asking a client to
 * declare whether anyone is a politically exposed person is ordinary due
 * diligence; telling them what the firm's screening found is the firm's own
 * finding, and the send time tipping off guard did not cover it (it reads SAR,
 * escalation and fraud wording). One of the lines, and the request email, also
 * carried an em dash.
 *
 * stripScreeningDisclosure removes only the clause that discloses a screening
 * result (a possible match, a hit, an alert, a result returned by our checks)
 * and keeps the question. A line left with nothing to ask is dropped. The
 * caller always tells the officer what was removed, so it is never silent.
 */
import { noteText } from './case-note-text.js';
import { clientText } from './client-copy.js';

const WHO = String.raw`(?:our|the|a|an|this|these|my)`;
const CHECK = String.raw`(?:screening|screen|checks?|sanctions(?: and PEP)? (?:screening|checks?)|PEP (?:screening|checks?)|watch ?list (?:screening|checks?)|searches|search|database checks?|compliance checks?)`;
const FOUND = String.raw`(?:returned|found|flagged|identified|showed|shows|produced|raised|came back with|threw up|picked up|generated|surfaced|highlighted|returns|has returned|have returned|indicated)`;
const RESULT = String.raw`(?:possible |potential |partial |name |close )?(?:match|matches|hit|hits|alert|alerts|result|results|flag|flags)`;
export const SCREENING_DISCLOSURE_RE = new RegExp(
  String.raw`\b${WHO}\s+${CHECK}\b[^.;!?]{0,80}?\b${FOUND}\b[^.;!?]{0,60}?\b${RESULT}\b` +
  String.raw`|\b(?:possible|potential|partial)\s+(?:PEP|sanctions?|watch ?list|adverse media)\s+(?:match|hit|alert)\b` +
  String.raw`|\bscreening\s+(?:match|hit|alert)\b` +
  String.raw`|\b(?:matched|appears?|appeared|came up|showed up|flagged)\s+(?:on|against|in)\s+(?:a |the |our )?(?:PEP|sanctions?|watch ?list|adverse media)\b`, 'i');

export function disclosesScreening(text) {
  return SCREENING_DISCLOSURE_RE.test(String(text == null ? '' : text));
}

const cap = (s) => s.replace(/^(\s*)([a-z])/, (m, a, b) => a + b.toUpperCase());
// Something still to ask or provide once the disclosure is gone.
const HAS_ASK = /\b(please|could you|can you|would you|confirm|provide|tell us|let us know|send|share|declare|whether|who|what|when|which|how)\b/i;

// { line, removed }: line is null when nothing is left to ask.
export function stripScreeningDisclosure(input) {
  const raw = String(input == null ? '' : input);
  if (!disclosesScreening(raw)) return { line: raw, removed: [] };
  const removed = [];
  // Sentences, then within a sentence the parts joined by a dash, a semicolon
  // or ", and" / ", but" / ", which" / ", so".
  const sentences = raw.match(/[^.!?]+[.!?]*\s*/g) || [raw];
  const kept = [];
  for (const s of sentences) {
    if (!disclosesScreening(s)) { kept.push(s.trim()); continue; }
    const parts = s.split(/\s*[–—]\s*|\s+-\s+|;\s*|,\s*(?=(?:and|but|which|so|as)\b)/i);
    const good = parts.filter(p => { if (disclosesScreening(p)) { removed.push(p.trim()); return false; } return true; })
      .map(p => p.replace(/^(?:and|but|which|so|as)\s+/i, '').trim()).filter(Boolean);
    if (good.length) {
      let t = good.join(', ');
      if (!/[.!?]$/.test(t)) t += /\?/.test(s) ? '?' : '.';
      kept.push(cap(t));
    }
  }
  const line = noteText(kept.join(' ').replace(/\s+/g, ' ').replace(/\s+([.,?!])/g, '$1').trim());
  return { line: line && HAS_ASK.test(line) ? line : null, removed };
}

// The draft as the officer will see it: every line without a screening result
// and without dashes. { lines, removed: [{ before, after, removed }] }.
export function cleanDraftLines(lines = []) {
  const out = [], removed = [];
  for (const l of (Array.isArray(lines) ? lines : [])) {
    const r = stripScreeningDisclosure(l);
    if (r.removed.length) removed.push({ before: l, after: r.line, removed: r.removed });
    // Fault 1: the client copy cleaner (noteText plus word hyphens), as every
    // line of a request goes to the client.
    if (r.line) out.push(clientText(r.line));
  }
  return { lines: out, removed };
}
